import { useRef } from 'react';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';

/**
 * Returns a stable boolean indicating whether the current route's page content
 * should be SSR-fetched (i.e., `ssr: true` on the Apollo query).
 *
 * The value is captured in a ref on first render so it never changes — this
 * prevents the query from re-firing when the slug changes during client-side
 * navigation.
 *
 * @param eventHandle - Event handle from the URL
 * @param pageSlug - The page slug(s) to match against. Pass a string for a
 *   single slug, an array to match any of several slugs (e.g., `['vip', 'weddingparty']`
 *   for pages that exist under both legacy and migrated-custom-page routes),
 *   or `'custom'` to match any custom page (migrated or `/page/<slug>`).
 */
export const useIsCurrentPageSsrFetchEnabled = (eventHandle: string, pageSlug: string | ReadonlyArray<string>): boolean => {
  const { currentPageSlug, isCustomPageSlug } = useCurrentPageSlug(eventHandle);

  const slugs = typeof pageSlug === 'string' ? [pageSlug] : pageSlug;
  const isMatch = slugs.some(slug => (slug === 'custom' ? isCustomPageSlug && !!currentPageSlug : currentPageSlug === slug));

  return useRef(isMatch).current;
};
